import { useEffect, useRef, useState } from "react";
import { Player, describeGap, type PlayerState } from "@backstory/player";
import type { GapSpan } from "@backstory/player";
import type { Chunk, ChunkRef, PrivacyMode } from "@backstory/protocol";
import { formatClock, privacyLabel } from "../lib/format";
import { Dialog } from "./ui";

export interface PlayerViewProps {
  refs: ChunkRef[];
  fetchChunk: (seq: number) => Promise<Chunk>;
  /** Called whenever the playhead moves (relative ms). */
  onTime?: (relMs: number) => void;
  onReady?: (player: Player) => void;
  privacyMode?: PrivacyMode;
  /** Identity for re-creating the player (session id). */
  sessionKey?: string;
  readOnly?: boolean;
  /** Hide the transport; used for a synchronized second surface. */
  controls?: boolean;
  /**
   * Absolute epoch ms this player should follow. A synchronized pane has no
   * transport of its own: it tracks the visit clock the primary pane drives.
   */
  syncAbs?: number;
  /** Whether the followed transport is playing, mirrored onto this player. */
  syncPlaying?: boolean;
  /** Caption shown above a synchronized pane. */
  label?: string;
  /** Keyboard shortcuts belong to the pane that owns the transport. */
  shortcuts?: boolean;
}

/** How far a synchronized pane may drift before it is pulled back in line. */
const SYNC_TOLERANCE_MS = 400;

const SPEEDS = [0.5, 1, 2, 4, 8];
const LS_SPEED = "backstory-player-speed";

function readSpeed(): number {
  try {
    const v = Number(localStorage.getItem(LS_SPEED));
    return SPEEDS.includes(v) ? v : 1;
  } catch {
    return 1;
  }
}

export function PlayerView({ refs, fetchChunk, onTime, onReady, privacyMode, sessionKey, readOnly, controls = true, syncAbs, syncPlaying, label, shortcuts = true }: PlayerViewProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const [state, setState] = useState<PlayerState>("idle");
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(readSpeed);
  const [skip, setSkip] = useState(true);
  const [scrubbing, setScrubbing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gaps, setGaps] = useState<readonly GapSpan[]>([]);
  const [startTs, setStartTs] = useState(0);
  const [help, setHelp] = useState(false);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const player = new Player(stage, { speed, skipInactivity: skip });
    playerRef.current = player;
    const offs = [
      player.on("statechange", ({ state }) => setState(state)),
      player.on("timeupdate", ({ currentTime, duration }) => {
        setTime(currentTime);
        setDuration(duration);
        onTime?.(currentTime);
      }),
      player.on("loaded", ({ duration }) => {
        setDuration(duration);
        setGaps(player.gaps);
        setStartTs(player.startTs);
      }),
      player.on("gap", () => setGaps([...player.gaps])),
      player.on("error", ({ error }) => setError((error as Error)?.message ?? String(error))),
    ];
    onReady?.(player);
    setError(null);
    player
      .loadManifest(refs, fetchChunk)
      // Pull the rest of the session in behind the opening frame so the DevTools
      // panels list the whole visit. Without it they only ever describe the few
      // seconds of chunks playback has reached, which reads as missing data.
      .then(() => player.hydrateAll())
      .catch((e) => setError((e as Error).message));
    return () => {
      offs.forEach((off) => off());
      player.destroy();
      playerRef.current = null;
    };
    // Re-create the player when the session (refs) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refs, sessionKey]);

  useEffect(() => {
    playerRef.current?.setSpeed(speed);
    try {
      localStorage.setItem(LS_SPEED, String(speed));
    } catch {
      /* ignore */
    }
  }, [speed]);
  useEffect(() => {
    playerRef.current?.setSkipInactivity(skip);
  }, [skip]);

  // A synchronized pane has no transport: it follows the visit clock, and is
  // nudged back only when it drifts, so it is not re-seeking on every frame.
  useEffect(() => {
    const p = playerRef.current;
    if (!p || syncAbs == null || !p.startTs) return;
    if (Math.abs(p.currentAbs - syncAbs) > SYNC_TOLERANCE_MS) void p.seek(syncAbs - p.startTs);
  }, [syncAbs]);

  useEffect(() => {
    const p = playerRef.current;
    if (!p || syncPlaying == null) return;
    if (syncPlaying) p.play();
    else p.pause();
  }, [syncPlaying, state]);

  // Keyboard shortcuts (ignored while typing in inputs).
  useEffect(() => {
    if (!shortcuts) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      const p = playerRef.current;
      if (!p) return;
      switch (e.key) {
        case " ":
        case "k":
          e.preventDefault();
          p.toggle();
          break;
        case "ArrowLeft":
        case "j":
          e.preventDefault();
          void p.seek(Math.max(0, p.currentTime - (e.shiftKey ? 30000 : 5000)));
          break;
        case "ArrowRight":
        case "l":
          e.preventDefault();
          void p.seek(Math.min(p.duration, p.currentTime + (e.shiftKey ? 30000 : 5000)));
          break;
        case ",":
          void p.step(-1);
          break;
        case ".":
          void p.step(1);
          break;
        case "-":
          setSpeed((s) => SPEEDS[Math.max(0, SPEEDS.indexOf(s) - 1)] ?? s);
          break;
        case "=":
        case "+":
          setSpeed((s) => SPEEDS[Math.min(SPEEDS.length - 1, SPEEDS.indexOf(s) + 1)] ?? s);
          break;
        case "i":
          setSkip((v) => !v);
          break;
        case "?":
          setHelp((v) => !v);
          break;
        case "Home":
          void p.seek(0);
          break;
        case "End":
          void p.seek(p.duration);
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shortcuts]);

  const seekFrac = (frac: number) => playerRef.current?.seek(frac * duration);
  const pct = (abs: number) => (duration ? `${Math.min(100, Math.max(0, ((abs - startTs) / duration) * 100))}%` : "0%");

  return (
    // Stage and transport are one layout child so the stage keeps a flexible
    // height of its own instead of collapsing when siblings are added above it.
    <div className="player" data-testid="player">
      {label && <div className="player-label muted small">{label}</div>}
      <div className="player-stage" ref={stageRef} role="img" aria-label={label ? `Replay of ${label}` : "Session replay"}>
        <span className={`badge state-pill ${state === "playing" ? "ok" : state === "buffering" || state === "loading" ? "info" : state === "unavailable" ? "warn" : "neutral"}`}>{state}</span>
        {privacyMode && (
          <span className={`badge ${privacyMode === "strict" ? "ok" : "neutral"}`} style={{ position: "absolute", top: 8, right: 80, zIndex: 2 }} title="Privacy mode this session was recorded with">
            {privacyLabel(privacyMode)}
          </span>
        )}
        {state === "loading" && !error && (
          <div className="stage-note" data-testid="player-loading" aria-busy="true">
            <span className="skeleton" style={{ width: 180, height: 12 }} />
            <span className="skeleton" style={{ width: 120, height: 12 }} />
          </div>
        )}
        {state === "unavailable" && !error && (
          <div className="stage-note" data-testid="player-unavailable">
            <strong>No replay data for this segment.</strong>
            <span className="muted small">Its chunks carry no page snapshot, so there is nothing to render. Events and DevTools still work.</span>
          </div>
        )}
        {error && <div className="error-box" style={{ position: "absolute", inset: 12, zIndex: 3 }}>Player error: {error}</div>}
      </div>
      {controls && (
        <div className="player-controls card">
          <button className="btn" onClick={() => playerRef.current?.toggle()} aria-label={state === "playing" ? "Pause" : "Play"}>
            {state === "playing" ? "❚❚" : "▶"}
          </button>
          <button className="btn ghost" onClick={() => void playerRef.current?.step(-1)} aria-label="Previous event">
            ⏮
          </button>
          <button className="btn ghost" onClick={() => void playerRef.current?.step(1)} aria-label="Next event">
            ⏭
          </button>
          <div className="scrub" style={{ position: "relative" }}>
            {gaps.map((g, i) => (
              <div
                key={i}
                className="gap"
                data-testid="scrub-gap"
                title={`Not recorded (${Math.round((g.end - g.start) / 1000)}s): ${describeGap(g.reason)}`}
                style={{ position: "absolute", top: 2, bottom: 2, left: pct(g.start), width: `calc(${pct(g.end)} - ${pct(g.start)})`, background: "repeating-linear-gradient(45deg, rgba(239,68,68,.35) 0 4px, transparent 4px 8px)", pointerEvents: "auto", borderRadius: 3 }}
              />
            ))}
            <input
              type="range"
              min={0}
              max={1000}
              value={duration ? Math.round((time / duration) * 1000) : 0}
              onPointerDown={() => setScrubbing(true)}
              onChange={(e) => {
                const frac = Number(e.target.value) / 1000;
                setTime(frac * duration);
                void seekFrac(frac);
              }}
              onPointerUp={() => setScrubbing(false)}
              aria-label="Seek"
              data-scrubbing={scrubbing || undefined}
              style={{ position: "relative", zIndex: 1, background: "transparent" }}
              autoComplete="off"
            />
          </div>
          <span className="time">
            {formatClock(time)} / {formatClock(duration)}
          </span>
          <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))} aria-label="Playback speed" autoComplete="off">
            {SPEEDS.map((s) => (
              <option key={s} value={s}>
                {s}×
              </option>
            ))}
          </select>
          <button className="btn ghost" aria-pressed={skip} onClick={() => setSkip(!skip)} title="Skip periods of inactivity (i)">
            Skip idle
          </button>
          {!readOnly && (
            <button className="btn ghost" onClick={() => setHelp(true)} aria-label="Keyboard shortcuts" title="Keyboard shortcuts (?)">
              ?
            </button>
          )}
        </div>
      )}
      <Dialog open={help} onClose={() => setHelp(false)} title="Keyboard shortcuts" width={420}>
        <div className="shortcuts">
          <span><span className="kbd">space</span> / <span className="kbd">k</span></span><span>Play / pause</span>
          <span><span className="kbd">←</span> / <span className="kbd">→</span></span><span>Back / forward 5 s (Shift: 30 s)</span>
          <span><span className="kbd">,</span> / <span className="kbd">.</span></span><span>Previous / next event</span>
          <span><span className="kbd">-</span> / <span className="kbd">=</span></span><span>Slower / faster</span>
          <span><span className="kbd">i</span></span><span>Toggle skip idle</span>
          <span><span className="kbd">Home</span> / <span className="kbd">End</span></span><span>Start / end</span>
          <span><span className="kbd">?</span></span><span>This help</span>
        </div>
      </Dialog>
    </div>
  );
}
